import {
    BlurEvent, ChangeEvent, ClickEvent, CrudDecorator, DataDisplayEvent, DataSource, DataSourceMode,
    DropdownSelectionEvent, Layout, LocationUtil, LookupModelSelectionEvent, Snackbar, Table, TableRow, Textbox,
    ValidationResult, LookupModelSearchEvent
} from "@mcleod/components";
import {
    ActionPermission, CityUtil, CurrencySettings, DatePart, DateUtil, FieldUpdateEvent, Model, ModelRow, ModelRowType,
    PermissionsUtil, StringUtil, Timezone
} from "@mcleod/core";
import { getDispatchControlBoolean, LMECompanySettings, ModelContact, ModelZones } from "@mcleod/general";
import { AppointmentUtil } from "./AppointmentUtil";
import { StopNotesAndReferenceNumbers } from "./StopNotesAndReferenceNumbers";
import { AutogenLayoutOrderStop } from "./autogen/AutogenLayoutOrderStop";
import { ModelLocation } from "./models/ModelLocation";
import { EdiUtility } from "@mcleod/datafusion/src/EdiUtility";
import { Location } from "./Location";

export class OrderStop extends AutogenLayoutOrderStop {
    commentsAndRefNbrs: StopNotesAndReferenceNumbers;
    private _validateApptChanges: boolean = false;
    private _loadingRecurringOrder: boolean = false;
    private _invokedFromEDI: boolean = false;
    private _slideOutTableRow: TableRow;
    public calculateRates: (checkDistance?: boolean) => void = () => { };
    public commodityChanged: () => void = () => { };
    public checkLocationIsTrailerPool: (usePool: boolean) => void = () => { };

    public get validateApptChanges(): boolean {
        return this._validateApptChanges;
    }

    public set validateApptChanges(value: boolean) {
        this._validateApptChanges = value;
    }

    public get loadingRecurringOrder(): boolean {
        return this._loadingRecurringOrder;
    }

    public set loadingRecurringOrder(value: boolean) {
        this._loadingRecurringOrder = value;
    }

    public get invokedFromEDI(): boolean {
        return this._invokedFromEDI;
    }

    public set invokedFromEDI(value: boolean) {
        this._invokedFromEDI = value;
    }

    public get slideOutTableRow(): TableRow {
        return this._slideOutTableRow;
    }

    public set slideOutTableRow(value: TableRow) {
        this._slideOutTableRow = value;
    }

    private _orderSource: DataSource;
    private _stopSource: DataSource;

    override onLoad(): void {
        this.mainDataSource.preventFocusFirstField = true;
        this.commentsAndRefNbrs = this.layoutStopNotesAndRefNbrs as StopNotesAndReferenceNumbers;
        this.textboxStopDateType.items = [LocationUtil.DATE_ON, LocationUtil.DATE_BETWEEN];
        this.textboxStopDateType.selectedItem = LocationUtil.DATE_BETWEEN;
        this.textboxSchedArriveLate.required = true;
        this.setupListeners();
        this.textboxSchedArriveLate.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(mode);
        };
    }

    setupListeners() {
        this.textboxContactName.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            const selectedRow = event.selection;
            if (selectedRow != null) {
                this.mainDataSource.activeRow.set("phone", selectedRow.get("phone"));
                this.mainDataSource.activeRow.set("contact_email", selectedRow.get("email"));
                this.mainDataSource.displayDataInBoundComponents();
            }
        });
        this.textboxSchedArriveEarly.addBlurListener(event => this.stopDateOnBlur(event));
        this.textboxSchedArriveLate.addBlurListener(event => this.stopDateOnBlur(event));
        this.textboxAddress.addBlurListener((event: BlurEvent) => {
            if (event.changedWhileFocused && this.citystate.textCombined.hasText()) {
                this.calculateRates();
            }
        });
        this.textboxLocationId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                this.setLocationTextAfterLocation(event.target, event.selection);
                const location_id = this.mainDataSource.activeRow?.get("location_id")
                if (location_id) {
                    Model.searchSingleRecord("lme/dispatch/location", { id: location_id, "updating_stop": true }).then(rowLocation => {
                        if (rowLocation != null) {
                            this.setDriverUnloadTextAfterLookup(new ModelRow(this.textboxDriverLoadUnload.lookupModel, false, this.getDriverLoadUnloadData(rowLocation)));
                        }
                    });
                }
                EdiUtility.checkDFXref(
                    this.textboxLocationCode.text,
                    this.textboxEdiLocCode.text,
                    this.mainDataSource,
                    this.orderSource,
                    this.dfXref,
                    this.owner,
                    this.stopSource,
                );
            }
        });
    }

    /** This is an event handler for the onBlur event of textboxSchedArriveLate and textboxSchedArriveEarly.  */
    stopDateOnBlur(event: BlurEvent) {
        AppointmentUtil.checkAppointment(event, this)
    }

    setLocationTextAfterLocation(textBox: Textbox, row: ModelRow, locationIdField?: string) {
        const data = row?.data
        const source = row?.data["source_name"];

        if (locationIdField == null)
            locationIdField = "id";

        this.citystate["_lookupModelDataForUpdate"] = null;

        if (source == "CITY") {
            textBox.text = CityUtil.formatCityStateZip(data?.["city_name"], data?.["state"], data?.["zip_code"]);
        } else {
            this.textboxLocationCode.text = data?.[locationIdField];
            this.textboxLocationCode.quickInfoOverrideKeyValue = data?.[locationIdField];
            this.textboxLocationName.displayData(this.mainDataSource.activeRow, null, 0);
            this.textboxAddress.displayData(this.mainDataSource.activeRow, null, 0);
            this.textboxAddress2.displayData(this.mainDataSource.activeRow, null, 0);
        }
        this.citystate.displayData(this.mainDataSource.activeRow, null, 0);
    }

    private setApptRequired(rowLocation: ModelRow<any>) {
        this.mainDataSource.activeRow.set("appt_required", rowLocation?.get("appt_required"));
        this.switchApptRequired.displayData(this.mainDataSource.activeRow, null, 0);
    }

    //initialized when invoked from EDI
    private initializeZoneId(tableRow: TableRow) {
        if(this.invokedFromEDI){
            const zoneId = tableRow.data.get("zone_id");
            if (zoneId != null) {
                new ModelZones().searchSingle({ id: zoneId }).then(response => {
                    if (response != null) {
                        const lmModelRow = new ModelRow(this.textboxZoneId.lookupModel, false, {
                            id: response.get("id"),
                            descr: response.get("descr")
                        });
                        lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                        tableRow.data.setLookupModelData("zone_id", lmModelRow);
                        tableRow.data.set("zone_id", response.get("id"));
                        this.textboxZoneId.displayData(tableRow.data, null, 0);
                    }
                });
            }
        }
    }

    initialize(isSlideIn: boolean, tableRow: TableRow, orderDataSource: DataSource, stopDataSource?: DataSource) {
        const mode = orderDataSource.mode;
        this.commonBilling(isSlideIn, tableRow, orderDataSource, stopDataSource, mode, null);
        this.initializeZoneId(tableRow); //initialized when invoked from EDI
    }

    initializeBilling(isSlideIn: boolean, tableRow: TableRow, orderDataSource: DataSource, stopDataSource: DataSource, mode: DataSourceMode, billingDataSource: DataSource) {
        this.commonBilling(isSlideIn, tableRow, orderDataSource, stopDataSource, mode, billingDataSource);
    }

    commonBilling(isSlideIn: boolean, tableRow: TableRow, orderDataSource: DataSource, stopDataSource: DataSource, mode: DataSourceMode, billingDataSource: DataSource) {
        this.layoutStopShowAsButton.orderSource = orderDataSource;
        this.layoutStopShowAsButton.stopRow = tableRow.data;
        this.panelAdditionalDetails.visible = isSlideIn;
        if (!isSlideIn) {
            this.textboxZoneId.printable = true;
        }
        const stopIndex = tableRow.index;
        const isPickupStop = stopIndex == 0;
        const stopType = tableRow.data.get("stop_type");

        this.mainDataSource.setRowsAndMode(mode, [tableRow.data]);
        OrderStop.setTableParentDataSource(this.commentsAndRefNbrs.tableStopComments, orderDataSource, mode, billingDataSource);
        OrderStop.setTableParentDataSource(this.commentsAndRefNbrs.tableStopReferenceNumbers, orderDataSource, mode, billingDataSource);
        this.commentsAndRefNbrs.stopRow = this.mainDataSource.activeRow;
        this.textboxLocationId.textCombined.manualAddLayout = null;
        if (this.mainDataSource.activeRow != null) {
            this.textboxLocationCode.text = this.mainDataSource.activeRow.get("location_id");
            this.textboxLocationCode.quickInfoOverrideKeyValue = this.textboxLocationCode.text;
        }

        this.setupReadOnlyStopTypes(stopType, isPickupStop);
        this.setStopDateCaptions(stopType);

        this.textboxLocationId.googlePlacesProps = {
            enableSearch: getDispatchControlBoolean("enable_google_places") && getDispatchControlBoolean("enable_stop_add_google"),
            createLocations: getDispatchControlBoolean("create_location_from_stop"),
            customerId: "",
            doBeforeGoogleLocationCreated: () => this.labelCreatingLocation.visible = true,
            doAfterGoogleLocationCreated: () => this.labelCreatingLocation.visible = false
        };

        if (!orderDataSource?.activeRow?.isNull("customer_id"))
            this.textboxLocationId.googlePlacesProps.customerId = orderDataSource?.activeRow?.get("customer_id");

        this.addFieldUpdateListener(this.mainDataSource.activeRow);

        if (!this.mainDataSource.activeRow._appending) {
            this.textboxStopDateType.selectedItem = this.mainDataSource.activeRow?.get("sched_arrive_late") != null ? LocationUtil.DATE_BETWEEN : LocationUtil.DATE_ON;

            if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UNCONFIRM_APPOINTMENTS) && this.activeRow.getBoolean("confirmed", false)) {
                this.switchConfirmed.enabled = false;
                this.switchConfirmed.disabledTooltip = "You do not have permission to unconfirm appointments";
            }
        }
        const updatingOrder = DataSourceMode.UPDATE === mode;
        if (updatingOrder) {
            this.textboxStopDateType.selectedItem =
                (this.mainDataSource.activeRow?.get("sched_arrive_early", null) && this.mainDataSource.activeRow?.get("sched_arrive_late") == null)
                    ? LocationUtil.DATE_ON
                    : LocationUtil.DATE_BETWEEN;
        }
        this.validateApptChanges = updatingOrder && this.mainDataSource.activeRow._appending == false;
        this.orderSource = orderDataSource;
        this.stopSource = stopDataSource;
        this.textboxEdiLocCode.visible = this.mainDataSource.activeRow?.get("is_edi_customer");
        EdiUtility.setXref(this.mainDataSource.activeRow, this.dfXref);
    }

    setupReadOnlyStopTypes(stopType: any, isInitialPickup: boolean = false) {
        if (isInitialPickup) {
            this.styleTextboxStopTypeReadOnly();
        } else if (stopType === "RP") {
            this.textboxStopType.text = "Reconsignment"
            this.styleTextboxStopTypeReadOnly();
        } else if (stopType === "SD" || stopType === "SP") {
            if (stopType === "SD") {
                this.textboxStopType.text = "Split Drop"
                this.imageYsplitDrop.visible = true;
            }
            else if (stopType == "SP") {
                this.textboxStopType.text = "Split Pickup"
                this.imageYSplitPick.visible = true;
            }
            this.textboxStopType.setSelectedItemFromValue(stopType);
            this.styleTextboxStopTypeReadOnly();
        }
    }

    styleTextboxStopTypeReadOnly() {
        this.textboxStopType.applyTempState({
            printable: true,
            color: "primary",
            fontSize: "xlarge",
            caption: null,
            paddingTop: 0,
            paddingBottom: 10,
            paddingLeft: 0
        });
    }

    addFieldUpdateListener(row: ModelRow) {
        if (row["_stopListener"] == null) { // keep a reference in each row to the listener to prevent adding multiple listeners if the same row is used somehow
            row["_stopListener"] = (event: FieldUpdateEvent) => {
                if (event.oldValue != event.newValue && !(StringUtil.isEmptyString(event.oldValue) && StringUtil.isEmptyString(event.newValue))) {
                    if (event.fieldName === "location_id") {
                        this.doAfterLocationIdChanged();
                    } else if (event.fieldName === "stop_type") {
                        this.calculateRates();
                    } else if (event.fieldName === "sched_arrive_early" && !DateUtil.datesEqual(event.oldValue, event.newValue) && CurrencySettings.getSingleton().multiCurrencyEnabled() && "A" == this.orderSource.activeRow.getString("status")) {
                        this.calculateRates(false);
                    } else if (event.fieldName === "city_id") {
                        this.calculateRates();
                        if (event.newValue == null) {
                            row.setLookupModelData("zone_id", null);
                            row.set("zone_id", null);
                            this.textboxZoneId.displayData(row, null, 0);
                        } else {
                            new ModelZones().searchSingle({ city_id: event.newValue }).then(response => {
                                if (response != null) {
                                    const lmModelRow = new ModelRow(this.textboxZoneId.lookupModel, false, {
                                        id: response.get("id"),
                                        descr: response.get("descr")
                                    });
                                    lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                                    row.setLookupModelData("zone_id", lmModelRow);
                                    row.set("zone_id", response.get("id"));
                                    this.textboxZoneId.displayData(row, null, 0);
                                }
                            });
                        }
                    }
                    if (event.fieldName == "sched_arrive_late") {
                        if (event.newValue != null && event.oldValue == null)
                            this.textboxStopDateType.selectedItem = LocationUtil.DATE_BETWEEN;
                        else if (event.newValue == null && event.oldValue != null && !this.textboxSchedArriveLate.hasText())
                            this.textboxStopDateType.selectedItem = LocationUtil.DATE_ON;
                    }
                }
                // Handle special case for sched_arrive_late when both values are null but field is empty
                // Since date fields have shortcuts, there was some odd behavior when manually entering dates
                else if (this.textboxStopDateType.selectedItem.equals(LocationUtil.DATE_BETWEEN) && event.fieldName == "sched_arrive_late"
                    && event.oldValue == null && event.newValue == null && !this.textboxSchedArriveLate.hasText()) {
                    this.textboxStopDateType.selectedItem = LocationUtil.DATE_ON;
                }
            }
            row.addAfterFieldUpdateListener(row["_stopListener"]);
        }
    }

    doAfterLocationIdChanged(isFromQuote: boolean = false, hasPortalContactInfo: boolean = false) {
        this.setFreeFormVisibility();
        this.citystate.displayData(this.mainDataSource.activeRow, null, 0);
        const location_id = this.mainDataSource.activeRow.get("location_id");
        if (!this.loadingRecurringOrder && !hasPortalContactInfo) this.setStopContactInfo();
        if (location_id) {
            new ModelLocation().searchSingle({
                id: location_id,
                "updating_stop": true
            }).then(async rowLocation => {
                if (rowLocation == null) {
                    Snackbar.showWarningSnackbar("Location not found in database.");
                    return;
                }
                if (!this.loadingRecurringOrder && this.orderSource?.activeRow?.isNull("recurring_order_id"))
                    this.setApptRequired(rowLocation);

                const tableRow: TableRow = TableRow.getContainingTableRow(this) ?? this.slideOutTableRow;

                const okToUpdateCommodity = [
                    this.orderSource.activeRow.get("commodity_id"),
                    this.orderSource.activeRow.get("commodity"),
                    this.orderSource.activeRow.get("temperature_min"),
                    this.orderSource.activeRow.get("temperature_max")
                ].every(val => val == null);

                if ((tableRow.index == 0) && okToUpdateCommodity && (rowLocation.get("def_commodity_id") != null)) {
                    this.orderSource.activeRow.set("commodity_id", rowLocation.get("def_commodity_id"));
                    this.commodityChanged();
                }

                if (tableRow.index == 0) {
                    this.activeRow.set("is_trailer_pool", rowLocation.get("is_trailer_pool", false));
                    const isPoolValue = rowLocation.get("is_trailer_pool", "N");
                    const usePool = isPoolValue == "Y" ? true : false;
                    this.checkLocationIsTrailerPool(usePool);
                }
                if (isFromQuote) {
                    this.setDefaultStopDateTypeQuote(rowLocation);
                }
                else {
                    this.setDefaultStopDateType(rowLocation);
                }

                // only apply the below logic for the first pickup
                if (tableRow.index != 0 || !LMECompanySettings.getSingleton().isBrokerageLtl() || this.invokedFromEDI) return;

                if (rowLocation.get("close_time") != null && this.textboxStopDateType.selectedItem.equals(LocationUtil.DATE_BETWEEN)) {
                    this.activeRow.set("sched_arrive_late", rowLocation.get("close_time"));
                    this.textboxSchedArriveLate.updateBoundData(this.mainDataSource.activeRow, this.mainDataSource.mode);
                } else {
                    this.activeRow.set("sched_arrive_late", null);
                }

                if (rowLocation.get("open_time") != null) {
                    this.activeRow.set("sched_arrive_early", rowLocation.get("open_time"));
                    this.textboxSchedArriveEarly.updateBoundData(this.activeRow, this.mainDataSource.mode);
                }

            });
        }
        else if (isFromQuote) {
            this.textboxStopDateType.selectedItem = LocationUtil.DATE_ON;
        } else {
            this.activeRow.set("latitude", null);
            this.activeRow.set("longitude", null);
            const tableRow: TableRow = TableRow.getContainingTableRow(this) ?? this.slideOutTableRow;
            if (tableRow.index == 0) {
                this.checkLocationIsTrailerPool(false);
            }
        }

        if (!this.invokedFromEDI) {
            this.setTimeTypeOnLocationChange(isFromQuote);
         }
    }

    setDefaultStopDateType(rowLocation: ModelRow) {
        //If values present, ignore location
        if (this.activeRow?.get("sched_arrive_early") != null) {
            this.textboxStopDateType.selectedItem = this.activeRow.get("sched_arrive_late") != null ? LocationUtil.DATE_BETWEEN : LocationUtil.DATE_ON;
        } else if (rowLocation != null) {
            this.textboxStopDateType.selectedItem = LocationUtil.getLocationDefaultStopType(rowLocation, this.activeRow.get("stop_type"))
        }
    }

    setDefaultStopDateTypeQuote(rowLocation: ModelRow) {
        this.textboxStopDateType.selectedItem = LocationUtil.getLocationDefaultStopTypeQuotes(rowLocation, this.activeRow.get("stop_type"));
    }

    initShipperStop(value: boolean) {
        if (value) {
            this.styleTextboxStopTypeReadOnly();
            this.textboxLocationId.visible = true;

            this.textboxLocationId.textCombined.manualAddLayout = null;
        }
    }

    public static setTableParentDataSource(table: Table, parentSource: DataSource, modeInbound?: DataSourceMode, billingSource?: DataSource) {
        if (parentSource != null) {
            table.dataSource.parentDataSource = billingSource ?? parentSource;
            const mode = modeInbound ? modeInbound : parentSource.mode;
            //if we are setting the mode to ADD, we don't need the blank/empty ModelRow that setting the mode normally will create
            //the fact that the creation of the blank row is async can only mess us up; it can cause existing rows to be wiped out
            //when the blank row is done creating (which causes the table's data to be redisplayed)
            if (mode !== DataSourceMode.ADD)
                table.dataSource.mode = mode;
            else
                table.dataSource.setRowsAndMode(mode, []);
        }
    }

    textboxStopTypeOnDropdownSelection(event: DropdownSelectionEvent) {
        this.setStopDateCaptions(event.selection?.value);
        if (event.userInitiatedChange && event.hasSelection()) {
            this.calculateRates();
            const location_id = this.activeRow?.get("location_id");
            if (location_id) {
                new ModelLocation().searchSingle({
                    id: location_id,
                    "updating_stop": true
                }).then(rowLocation => {
                    if (rowLocation != null) {
                        this.setDriverUnloadTextAfterLookup(new ModelRow(this.textboxDriverLoadUnload.lookupModel, false, this.getDriverLoadUnloadData(rowLocation)));
                        this.setDefaultStopDateType(rowLocation);
                    }
                })
            }
        }
    }

    setStopDateCaptions(stopType: string) {
        const caption = "SO" === stopType ? "Delivery"
            : "PU" === stopType ? "Pickup"
                : "Scheduled"
        const isDateWindow = this.textboxStopDateType.selectedItem == LocationUtil.DATE_BETWEEN;

        this.textboxStopDateType.caption = caption;
        this.textboxSchedArriveEarly.caption = isDateWindow ? `${caption} Window` : `${caption} Appointment`;
    }

    getDriverLoadUnloadData(rowLocation: ModelRow): any {
        const loadUnloadField = this.isDelivery() ? "driver_unload_id" : "driver_load_id";
        let loadUnloadModelRowData = rowLocation.getFirstLookupModelData(loadUnloadField);
        if (loadUnloadModelRowData == null)
            loadUnloadModelRowData = this.textboxDriverLoadUnload.createLookupModelRow({ descr: 'No driver loading or unload', id: 'N' });
        else if (loadUnloadModelRowData instanceof ModelRow)
            loadUnloadModelRowData = loadUnloadModelRowData.data;
        return loadUnloadModelRowData;
    }

    setDriverUnloadTextAfterLookup(driverUnloadLookupData: ModelRow) {
        this.mainDataSource.activeRow.setLookupModelData("driver_load_unload", driverUnloadLookupData);
        this.mainDataSource.activeRow.set("driver_load_unload", driverUnloadLookupData.get("id"));
        if (this.mainDataSource.activeRow._appending)
            this.textboxDriverLoadUnload.displayData(this.mainDataSource.activeRow, null, 0);
    }

    switchApptRequiredOnChange(event) {
        const apptReqd = getDispatchControlBoolean("appt_confirmation", false) && this.switchApptRequired.checked && !this.mainDataSource.activeRow._appending;
        this.switchConfirmed.visible = this.textboxApptStatus.visible = apptReqd;
        if(!apptReqd) this.textboxApptStatus.clear();
    }

    switchPalletsRequiredOnChange(event) {
        this.textboxPalletsHowMany.visible = this.switchPalletsRequired.visible && this.switchPalletsRequired.checked;
    }

    isDelivery(): boolean {
        return "SO" === this.activeRow.getString("stop_type");
    }

    getData(): ModelRow<any> {
        return TableRow.getContainingTableRow(this)?.data;
    }

    /** This is an event handler for the onChange event of textboxStopDateType.  */
    textboxStopDateTypeOnChange(event: ChangeEvent) {
        const between = event.newValue === LocationUtil.DATE_BETWEEN.displayValue;
        const lateNoLongerVisible = (event.userInitiatedChange || this.panelLateArrival.visible) && !between;
        this.panelLateArrival.visible = between;
        this.textboxSchedArriveLate.required = false;
        if (between) {
            this.textboxSchedArriveLate["_captionLabel"].preventCollapse = true;
            this.textboxSchedArriveLate["_captionLabel"].visible = false;
            this.textboxSchedArriveLate.required = true;
            this.textboxSchedArriveLate.placeholder = "Required";
        }
        if (lateNoLongerVisible) {
            this.mainDataSource.activeRow?.set("sched_arrive_late", null);
            this.textboxSchedArriveEarly.validateSimple(false, true);
        }
        this.setStopDateCaptions(this.activeRow?.getString("stop_type", null));
    }

    private setStopContactInfo() {
        const location_id = this.mainDataSource.activeRow.get("location_id")
        if (!location_id) {
            if (!this.invokedFromEDI) {
                this.mainDataSource.activeRow.setLookupModelData("contact_name", null);
                this.mainDataSource.activeRow.set({ "contact_name": null, "phone": null, "contact_email": null });
                this.textboxContactName.displayData(this.mainDataSource.activeRow, null, 0);
                this.textboxPhone.displayData(this.mainDataSource.activeRow, null, 0)
                this.textboxContactEmail.displayData(this.mainDataSource.activeRow, null, 0)
            }
        } else {
            new ModelContact().searchSingle({ "parent_row_type": "L", "parent_row_id": location_id }).then(result => {

                const contactData = result?.data;
                if (!contactData)
                    return;

                this.mainDataSource.activeRow.setLookupModelData("contact_name", contactData);

                if (contactData["name"]) {
                    this.mainDataSource.activeRow.set("contact_name", contactData["name"]);
                    this.textboxContactName.validate(false, false);
                    this.textboxContactName.displayData(this.mainDataSource.activeRow, null, null);
                }

                if (contactData["phone"]) {
                    this.mainDataSource.activeRow.set("phone", contactData["phone"]);
                    this.textboxPhone.validate(false, false);
                    this.textboxPhone.displayData(this.mainDataSource.activeRow, null, null);
                }

                if (contactData["email"]) {
                    const firstEmail = this.getFirstEmail(contactData["email"]);
                    this.mainDataSource.activeRow.set("contact_email", firstEmail);
                    this.textboxContactEmail.validate(false, false);
                    this.textboxContactEmail.displayData(this.mainDataSource.activeRow, null, null);
                }
            });
        }

    }

    getFirstEmail(inputEmail: String): String {
        if (inputEmail) {
            const scrubbedEmail = inputEmail.replace(",", ";");
            const aryEmails = scrubbedEmail.split(";");
            return aryEmails[0];
        }
        else {
            return "";
        }
    }

    textboxContactNameBeforeLookupModelSearch(event) {
        if (this.mainDataSource.activeRow.get("location_id")) {
            event.filter.parent_row_type = "L";
            event.filter.parent_row_id = this.mainDataSource.activeRow.get("location_id");
            event.filter.is_active = "Y";
        } else {
            event.preventDefault();
        }
    }

    setFreeFormVisibility() {
        const locationIdExists: boolean = this.mainDataSource.activeRow.get("location_id") == null;
        this.citystate.enabled = locationIdExists;
        this.citystate.enabledDuringAdd = locationIdExists;
        this.citystate.enabledDuringUpdate = locationIdExists;
    }

    /** This is an event handler for the onChange event of switchConfirmed.  */
    switchConfirmedOnChange(event: ChangeEvent) {
        const changedData = this.mainDataSource.activeRow.getChangedData();
        if (event.userInitiatedChange && this.validateApptChanges
            && changedData["confirmed"] != null && changedData["confirmed"] === "N") {
            AppointmentUtil.showAppointmentChangeDialog({ "confirmed": "Y" }, this.sourceAppointmentChange, this.mainDataSource.activeRow, { switchConfirmed: this.switchConfirmed }).then(() => {
                this.switchApptRequired.enabled = !this.activeRow?.getBoolean("confirmed");
            });
        } else {
            this.switchApptRequired.enabled = !this.activeRow?.getBoolean("confirmed");
        }
    }

    getAppointmentChangedRow(): ModelRow<any> {
        const changedValues = this.mainDataSource.activeRow.getChangedData();
        if (this.apptChanged(changedValues)) {
            const row = this.mainDataSource.activeRow.get("appointment_change", null);
            if (row != null) {
                row.set("unconfirmed", changedValues["confirmed"] != null && changedValues["confirmed"] === "N");
                row.set("stop_id", this.mainDataSource.activeRow?.get("id"));
                row.set("sequence", this.mainDataSource.activeRow?.get("order_sequence"));
                if (changedValues["sched_arrive_early"] != null)
                    row.set("revised_early", this.mainDataSource.activeRow?.get("sched_arrive_early"));
                if (changedValues["sched_arrive_late"] != null)
                    row.set("revised_late", this.mainDataSource.activeRow?.get("sched_arrive_late"));
            }
            return row;
        }
    }

    private apptChanged(changedData: any) {
        return this.validateApptChanges && this.mainDataSource.activeRow.get("appointment_change", null) != null &&
            ((changedData["confirmed"] != null && changedData["confirmed"] === "N") ||
                (AppointmentUtil.isApptConfirmed(this.mainDataSource.activeRow) && (changedData["sched_arrive_early"] != null || changedData["sched_arrive_late"] != null)))
    }

    /** This is an event handler for the afterModeChange event of mainDataSource.  */
    sourceStopAfterModeChange(event) {
        this.validateApptChanges = event.newMode === DataSourceMode.UPDATE;
    }

    sourceStopAfterExecution(event) {
        this.setFreeFormVisibility();
    }

    private setTimezone(comp: Textbox) {
        const abbr = this.mainDataSource.activeRow?.get("timezone_id");
        comp.timezone = Timezone.getFromAbbreviation(abbr);
    }

    /** This is an event handler for the onDataDisplay event of citystate.  */
    citystateOnDataDisplay(event: DataDisplayEvent) {
        this.setTimezone(this.textboxSchedArriveEarly);
        this.setTimezone(this.textboxSchedArriveLate);
    }

    private dateValidator(mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (!StringUtil.isEmptyString(this.textboxSchedArriveEarly.text) && !StringUtil.isEmptyString(this.textboxSchedArriveLate.text)) {
            const arrivalDate = DateUtil.parseDateWithKeywords(this.textboxSchedArriveEarly.text, true, true);
            const departureDate = DateUtil.parseDateWithKeywords(this.textboxSchedArriveLate.text, true, true);
            const dayDiff = DateUtil.dateDiff(DatePart.SECOND, departureDate, arrivalDate);

            if (dayDiff < 0) {
                return {
                    component: this.textboxSchedArriveEarly,
                    isValid: false,
                    validationMessage: "The early date cannot be later than the late date."
                };
            }
        }

        return null;
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        super.displayComponentData(data, allData, rowIndex);
        // When a stop is deleted from the Orders page, each row in the stops table is rebuilt and
        // textboxStopDateType.selectedItem selected by the user is wiped out.
        this.setDefaultStopDateType(null);
    }

    onComponentValidationFailed(result: ValidationResult) {
        const comp = result.component;
        const stopPrefix = this.getStopPrefix();
        result.caption = stopPrefix + result.caption;
        if (comp == this.textboxSchedArriveLate) {
            if (this.textboxSchedArriveEarly.validationWarning) {
                // "Pickup/Delivery Window" will already be displayed as missing or invalid field
                result.caption = null;
            } else {
                result.caption = stopPrefix + this.textboxSchedArriveEarly.caption;
            }
        } else {

            if (comp == this.citystate && this.textboxLocationId.required) {
                result.caption = null;
            } else {
                if (comp.parent == this.panelStopFreeForm && !this.panelStopFreeForm.visible)
                    result.componentToFocus = this.textboxLocationId;
            }
        }
    }

    getStopPrefix() {
        const tableRow = TableRow.getContainingTableRow(this);
        if (tableRow != null) {
            if (tableRow.index == 0)
                return "Origin ";
            else if (tableRow.index == tableRow.table.rowCount - 1)
                return "Destination ";
            return `Stop ${(tableRow.index + 1)} `;
        }
        return "";
    }

    async ediLocCodeOnBlur(event: BlurEvent) {
        const textBox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textBox);
        await EdiUtility.setXref(tableRow.data, this.dfXref);
    }

    async dfXrefOnClick() {
        EdiUtility.dfXrefOnClick(this.mainDataSource, this.orderSource, this.dfXref, this.owner, this.stopSource);
    }

    public get orderSource(): DataSource {
        return this._orderSource;
    }

    public set orderSource(value: DataSource) {
        this._orderSource = value;
    }

    public get stopSource(): DataSource {
        return this._stopSource;
    }

    public set stopSource(value: DataSource) {
        this._stopSource = value;
    }

    public setStopDateTypeForEdi() {
        this.textboxStopDateType.selectedItem = this.mainDataSource.activeRow?.get("sched_arrive_late") != null ? LocationUtil.DATE_BETWEEN : LocationUtil.DATE_ON;
    }

    /** This is an event handler for the afterDropdownSelection event of textboxStopType. */
    textboxStopTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        const curStopType = event.target.dataSource.activeRow.get("stop_type");
        const location_id = this.mainDataSource.activeRow?.get("location_id")

        if (curStopType && location_id) {
            this.updateStopLocationTimeType(curStopType, location_id);
        }
    }

    updateStopLocationTimeType(curStopType: string, location_id: string, isFromQuote: boolean = false) {
        Model.searchSingleRecord("lme/dispatch/location", { id: location_id, "updating_stop": true }).then(rowLocation => {
            if (rowLocation == null) {
                return;
            }
            if (isFromQuote) {
                this.textboxStopDateType.selectedItem = LocationUtil.getLocationDefaultStopTypeQuotes(rowLocation, curStopType);
            } else {
                this.textboxStopDateType.selectedItem = LocationUtil.getLocationDefaultStopType(rowLocation, curStopType);
            }
        });
    }


    /** This is an event handler for the onChange event of textboxLocationCode. */
    setTimeTypeOnLocationChange(isFromQuote: boolean = false) {
        const curStopType = this.mainDataSource.activeRow?.get("stop_type");
        const location_id = this.mainDataSource.activeRow?.get("location_id");

        if (curStopType && location_id) {
            this.updateStopLocationTimeType(curStopType, location_id, isFromQuote);
        }
    }

    /** This is an event handler for the onClick event of buttonAddLocation. */
    buttonAddLocationOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/dispatch/Location") as Location;
        const crudPanel = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSaveAndClose: true,
                showSave: false
            },
           doAfterSlideOut : async () => {
                const tableRow = layout.activeRow as ModelRow;
                if(tableRow) {
                    const savedLocId = tableRow.get("id");
                    if (savedLocId) {
                        this.mainDataSource.activeRow.set("location_id", savedLocId);
                        this.mainDataSource.activeRow.set("location_name", tableRow.get("name"));
                        this.mainDataSource.activeRow.set("address", tableRow.get("address1"));
                        this.mainDataSource.activeRow.set("address2", tableRow.get("address2"));
                        this.mainDataSource.activeRow.set("city_name", tableRow.get("city_name"));
                        this.mainDataSource.activeRow.set("state", tableRow.get("state"));
                        this.mainDataSource.activeRow.set("zip_code", tableRow.get("zip_code"));
                        this.mainDataSource.activeRow.set("city_id", tableRow.get("city_id"));
                        this.mainDataSource.redisplayRow(0, this.mainDataSource.activeRow, null);
                    }
                }
            }
        });
        crudPanel.slideIn();
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDriverLoadUnload. */
    textboxDriverLoadUnloadBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }
}
